import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Target, FloppyDisk, ChartLine, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SectionHeader from "@/components/SectionHeader";

/**
 * Operations Budget & Dashboard Real-vs-Budget.
 *  - Tab "Captura": tabla 12 meses × 5 métricas editable
 *  - Tab "Dashboard": comparativa Real vs Budget con variance %
 * Métricas: metros, motoniveladora_hours, truck_tons, scoop_tons, scoop_hours.
 */

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const METRICS = [
  // Nuevos headers pedidos por el usuario (12/Feb/2026)
  { k: "meters_total", l: "Total metros", u: "m", g: "produccion" },
  { k: "meters_capex", l: "CAPEX (m)", u: "m", g: "produccion" },
  { k: "meters_opex", l: "OPEX (m)", u: "m", g: "produccion" },
  { k: "efficiency_pct", l: "% Eficiencia", u: "%", g: "eficiencia" },
  { k: "overbreak_pct", l: "% Overbreak", u: "%", g: "eficiencia" },
  { k: "underbreak_pct", l: "% Underbreak", u: "%", g: "eficiencia" },
  { k: "ton_mineral", l: "Ton mineral", u: "t", g: "material" },
  { k: "ton_tepetate", l: "Ton tepetate", u: "t", g: "material" },
  // Legacy — se mantienen para retrocompatibilidad
  { k: "motoniveladora_hours", l: "Motoniv (h)", u: "h", g: "equipos" },
  { k: "truck_tons", l: "Camión (t)", u: "t", g: "equipos" },
  { k: "scoop_tons", l: "Scoop (t)", u: "t", g: "equipos" },
  { k: "scoop_hours", l: "Scoop (h)", u: "h", g: "equipos" },
];

const emptyRow = () => Object.fromEntries(METRICS.map((m) => [m.k, ""]));

export default function OperationsBudgetTab({ canWrite }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState(() => Array.from({ length: 12 }, emptyRow));
  const [busy, setBusy] = useState(false);
  const [dash, setDash] = useState(null);

  const loadBudgets = useCallback(async () => {
    try {
      const r = await api.get(`/operations/budgets?year=${year}`);
      const next = Array.from({ length: 12 }, emptyRow);
      (r.data || []).forEach((b) => {
        const i = (b.month || 1) - 1;
        METRICS.forEach((m) => { next[i][m.k] = b[m.k] ?? ""; });
      });
      setRows(next);
    } catch (e) { toast.error(apiError(e)); }
  }, [year]);

  const loadDashboard = useCallback(async () => {
    try {
      const r = await api.get(`/operations/budget/dashboard?year=${year}`);
      setDash(r.data);
    } catch (e) { setDash(null); }
  }, [year]);

  useEffect(() => { loadBudgets(); loadDashboard(); }, [loadBudgets, loadDashboard]);

  const setCell = (mIdx, key, val) => {
    const next = [...rows];
    next[mIdx] = { ...next[mIdx], [key]: val };
    setRows(next);
  };

  const saveAll = async () => {
    setBusy(true);
    let ok = 0, err = 0;
    for (let i = 0; i < 12; i++) {
      const row = rows[i];
      const anyValue = METRICS.some((m) => row[m.k] !== "" && row[m.k] !== null && !Number.isNaN(Number(row[m.k])));
      if (!anyValue) continue;
      try {
        // Enviamos TODOS los campos del nuevo esquema; el backend deriva
        // meters_capex_pct desde el split (capex/opex) automáticamente.
        const payload = {
          year: Number(year), month: i + 1,
        };
        METRICS.forEach((m) => {
          payload[m.k] = Number(row[m.k] || 0);
        });
        // Clamp para %
        ["efficiency_pct", "overbreak_pct", "underbreak_pct"].forEach((k) => {
          payload[k] = Math.max(0, Math.min(100, payload[k]));
        });
        await api.put("/operations/budgets", payload);
        ok += 1;
      } catch (_e) { err += 1; }
    }
    setBusy(false);
    if (err) toast.error(`Guardados ${ok} · fallidos ${err}`);
    else toast.success(`Budget ${year} guardado (${ok} meses)`);
    loadDashboard();
  };

  const yearOpts = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1, y + 2];
  }, [now]);

  return (
    <div className="space-y-4" data-testid="ops-budget-panel">
      <SectionHeader icon={Target} title="Budget Operacional" eyebrow="Metas mensuales · Real vs Budget en tiempo real" testid="ops-budget-header"
        right={
          <>
            <div>
              <Label className="overline-label">Año</Label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                      data-testid="ops-budget-year" className="h-9 px-2 border border-slate-300 rounded text-sm">
                {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {canWrite && (
              <Button onClick={saveAll} disabled={busy} data-testid="ops-budget-save" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                <FloppyDisk size={14} weight="bold" /> {busy ? "Guardando…" : "Guardar budget"}
              </Button>
            )}
          </>
        } />

      <Tabs defaultValue="capture">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="capture" data-testid="ops-budget-tab-capture">Captura</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="ops-budget-tab-dashboard">
            <ChartLine size={13} weight="bold" className="mr-1" /> Dashboard Real vs Budget
          </TabsTrigger>
        </TabsList>

        <TabsContent value="capture" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white text-[10px] uppercase font-bold">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-900">Mes</th>
                  {METRICS.map((m) => (
                    <th key={m.k} className="text-center px-3 py-2">{m.l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1.5 font-bold text-slate-800 sticky left-0 bg-white">{MONTHS[i]}</td>
                    {METRICS.map((m) => (
                      <td key={m.k} className="px-2 py-1.5">
                        <Input type="number" step="any" min={0}
                          disabled={!canWrite}
                          value={row[m.k]}
                          onChange={(e) => setCell(i, m.k, e.target.value)}
                          className="h-8 text-xs text-right tabular-nums"
                          data-testid={`ops-budget-${i + 1}-${m.k}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 italic">Solo se guardan los meses con al menos una métrica &gt; 0. Las celdas vacías se ignoran.</p>
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4">
          {!dash ? (
            <p className="text-sm text-slate-400 text-center py-10 italic">Sin datos</p>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs" data-testid="ops-budget-dashboard-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="text-left px-2 py-2">Mes</th>
                    {METRICS.map((m) => <th key={m.k} className="text-center px-2 py-2" colSpan={3}>{m.l}</th>)}
                  </tr>
                  <tr className="bg-slate-800 text-slate-200 text-[9px] uppercase">
                    <th className="px-2 py-1"></th>
                    {METRICS.map((m) => (
                      <>
                        <th key={`${m.k}-b`} className="px-2 py-1">Budget</th>
                        <th key={`${m.k}-r`} className="px-2 py-1">Real</th>
                        <th key={`${m.k}-v`} className="px-2 py-1">Δ%</th>
                      </>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dash.months.map((m) => (
                    <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ops-budget-dash-row-${m.month}`}>
                      <td className="px-2 py-1.5 font-bold text-slate-800">{MONTHS[m.month - 1]}</td>
                      {METRICS.map((mm) => {
                        const b = m.budget[mm.k]; const r = m.real[mm.k]; const v = m.variance_pct[mm.k];
                        return (
                          <>
                            <td key={`${mm.k}-b`} className="px-2 py-1.5 text-right tabular-nums text-slate-500">{b ? b.toLocaleString() : "—"}</td>
                            <td key={`${mm.k}-r`} className="px-2 py-1.5 text-right tabular-nums font-semibold">{r ? r.toLocaleString() : "—"}</td>
                            <td key={`${mm.k}-v`} className={`px-2 py-1.5 text-right tabular-nums font-bold text-[11px] ${v === null ? "text-slate-300" : v >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                              {v === null ? "—" : (
                                <span className="inline-flex items-center gap-0.5">
                                  {v >= 0 ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
                                  {Math.abs(v)}%
                                </span>
                              )}
                            </td>
                          </>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
                    <td className="px-2 py-2 text-slate-900">Total {year}</td>
                    {METRICS.map((mm) => {
                      const b = dash.totals.budget[mm.k]; const r = dash.totals.real[mm.k]; const v = dash.totals.variance_pct[mm.k];
                      return (
                        <>
                          <td key={`${mm.k}-tb`} className="px-2 py-2 text-right tabular-nums text-slate-600">{b ? b.toLocaleString() : "—"}</td>
                          <td key={`${mm.k}-tr`} className="px-2 py-2 text-right tabular-nums text-slate-900">{r ? r.toLocaleString() : "—"}</td>
                          <td key={`${mm.k}-tv`} className={`px-2 py-2 text-right tabular-nums ${v === null ? "text-slate-400" : v >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {v === null ? "—" : `${v >= 0 ? "+" : ""}${v}%`}
                          </td>
                        </>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-slate-500 italic mt-2">
            Real se calcula desde <b>operations_entries</b>: metros (anclador+jumbo), motoniv horas, camión toneladas, scoop toneladas+horas. Δ% positivo = sobre meta ✓
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
